import { QueryMetricsRequest } from "@/shared/domain/metrics";
import type { Condition } from "@/types";

/**
 * How many sessions a raw chart card loads before the user asks for the rest.
 *
 * There is exactly one metric row per session, so a raw card's total is the
 * session count in the window — tens of thousands over a wide range. A page
 * this size (~400 KB on the wire, 1-3 points per pixel on a wide card) covers
 * most cards outright while keeping first paint to a single request; anything
 * beyond it is opt-in through the card's "Load all" control.
 */
export const RAW_PAGE_SIZE = 1000;

/** Everything that identifies one raw chart's slice of metric data. Note the
 *  absent `period` and `aggregation`: raw mode buckets nothing, so the
 *  dashboard's period does not enter the request (and changing it does not
 *  refetch a raw card). */
export interface RawQueryInputs {
  metricName: string;
  /** Inclusive lower bound, Unix-epoch nanoseconds. */
  startTimeNs: number;
  /** Exclusive upper bound, Unix-epoch nanoseconds. */
  endTimeNs: number;
  deviceIds?: string[];
  sessionIds?: string[];
  /** Session-property / custom-field predicate applied server-side. See
   *  {@link AggregateQueryInputs.condition}: a raw card is filtered by the
   *  dashboard's filter bar exactly as an aggregated one is, so the two
   *  variants never disagree about which sessions are in scope. */
  condition?: Condition;
}

/**
 * The paginated raw query a raw chart card is drawn from: one row per session
 * in the window, newest first.
 *
 * Shared between the chart and the card around it — which reads the same
 * pages to caption the plot and to offer "Load all" — for the same reason
 * {@link aggregateRequestFor} is shared: an identical request means an
 * identical React Query key, so the two hold one cache entry and one fetch,
 * and `fetchNextPage` from the card lands in the series the chart draws.
 *
 * `descending` is what makes the first page the RECENT sessions rather than
 * the oldest ones; the chart sorts back to ascending before plotting.
 */
export function rawRequestFor(inputs: RawQueryInputs): QueryMetricsRequest {
  return {
    name: inputs.metricName,
    time_filter: "end_time",
    // The dashboard's bounds are `number` ns, which past 2022 is a rounded
    // double; `BigInt()` throws RangeError on a non-integral one, so truncate
    // first. The lost sub-microsecond is far below the resolution of a window
    // measured in days.
    start_time_ns: BigInt(Math.trunc(inputs.startTimeNs)),
    end_time_ns: BigInt(Math.trunc(inputs.endTimeNs)),
    max_results: RAW_PAGE_SIZE,
    descending: true,
    include_device_ids: inputs.deviceIds,
    include_session_ids: inputs.sessionIds,
    condition: inputs.condition,
  };
}
